import { OfferT } from "../../../domain";
import React, { useMemo } from "react";

type ScoreItemProps = {
  amount: number;
  percentage: number;
};

const ScoreItem = ({
  amount,
  percentage,
}: ScoreItemProps): React.ReactElement => {
  const percentageColor = useMemo(
    () => (percentage < 0 ? "text-[#F44336]" : "text-[#333333]"),
    [percentage]
  );

  return (
    <section className="grid grid-cols-[1fr] items-center gap-[8px]">
      <section className="grid grid-cols-[1fr_auto] gap-[4px_8px]">
        <span className="whitespace-nowrap text-left text-[10px] text-[#484848]">
          <span className="font-bold text-[#333333]">
            ${amount.toLocaleString("en-US")}
          </span>{" "}
          carrier pay ={" "}
          <span className={`${percentageColor} font-bold`}>{percentage}%</span>
        </span>
      </section>
    </section>
  );
};

type ScoreProps = {
  offer: OfferT;
};

const OfferCardMarginTooltip = ({ offer }: ScoreProps): React.ReactElement => {
  const data = useMemo(() => {
    return [
      {
        amount: Math.round(offer.total_charge * 0.8),
        percentage: 20,
      },
      {
        amount: Math.round(offer.total_charge * 0.85),
        percentage: 15,
      },
      {
        amount: Math.round(offer.total_charge * 0.9),
        percentage: 10,
      },
    ];
  }, [offer.total_charge]);

  const currentData = useMemo(() => {
    const margin = offer.total_charge - offer.rate;
    const marginPercentage = Math.round((100 * margin) / offer.total_charge);

    return { amount: offer.rate, percentage: marginPercentage };
  }, [offer]);

  if (!offer.total_charge) {
    return (
      <section className="absolute bottom-full left-[50%] z-[500] mb-[10px] hidden w-[250px] translate-x-[-50%] gap-[8px] rounded-[5px] border-[1px] border-solid border-[#D6D6D6] bg-[#FFFFFF] p-[8px] shadow-[2px_2px_2px_rgba(0,0,0,0.15)] group-hover/rateScores:grid">
        <span className="text-center text-[10px] text-[#484848]">
          No customer rate available to calculate margin
        </span>
      </section>
    );
  }

  return (
    <section className="absolute bottom-full left-[50%] z-[500] mb-[10px] hidden translate-x-[-50%] gap-[8px] rounded-[5px] border-[1px] border-solid border-[#D6D6D6] bg-[#FFFFFF] p-[8px] shadow-[2px_2px_2px_rgba(0,0,0,0.15)] group-hover/rateScores:grid">
      {data.map((item, key) => (
        <ScoreItem key={key} {...item} />
      ))}
      <span className="block h-[1px] w-full border-b-[1px] border-solid border-[#D6D6D6]" />
      <span className="text-[10px] text-[#484848]">
        Carrier&lsquo;s actual offer{" "}
      </span>
      <ScoreItem {...currentData} />
    </section>
  );
};

type OfferCardMarginTooltipContainerProps = {
  children: React.ReactNode;
};

const OfferCardMarginTooltipContainer = ({
  children,
}: OfferCardMarginTooltipContainerProps): React.ReactElement => {
  return (
    <section className="group/rateScores relative cursor-pointer">
      {children}
    </section>
  );
};

export { OfferCardMarginTooltipContainer, OfferCardMarginTooltip };
